import React from "react"
import { FaFacebook, FaLink, FaTwitterSquare } from "react-icons/fa"

import useGtm from "@src/hooks/use-gtm"

import classNames from "@src/utilities/class-names"

export interface ShareBarProps {
  title: string
  url: string
}

const linkButtonClassName = classNames(
  "bg-transparent",
  "block",
  "border-0",
  "h-11",
  "hover:text-orange",
  "relative",
  "p-0",
  "text-black",
  "transition-colors",
  "w-11"
)

const svgClassName = classNames(
  "absolute",
  "block",
  "h-1/2",
  "left-0",
  "top-1/2",
  "-translate-y-1/2",
  "w-1/2"
)

const textAreaClassName = classNames(
  "absolute",
  "[clip-path:circle(0)]",
  "-left-[1000rem]",
  "-top-[1000rem]",
  "z-[-1]"
)

export default function ShareBar({ title, url }: ShareBarProps) {
  const copyRef = React.useRef<HTMLTextAreaElement>(null)
  const [isCopied, setIsCopied] = React.useState<boolean>(false)

  const copyUrlToClipboard: React.MouseEventHandler<HTMLButtonElement> = () => {
    if (copyRef && copyRef.current) {
      copyRef.current.select()
      document.execCommand("copy")
      setIsCopied(true)
      useGtm({
        event: "orTrack",
        eventName: "Article Shared",
        properties: {
          method: "Copied link",
          title,
        },
      })
    }
  }

  React.useEffect(() => {
    if (isCopied === true) {
      setTimeout(() => {
        setIsCopied(false)
      }, 1900)
    }
  }, [isCopied])

  return (
    <ul
      className="flex list-style-none justify-start"
      data-component="journal-share-bar"
    >
      <li>
        <a
          aria-label="Share on Facebook"
          className={linkButtonClassName}
          href={`http://www.facebook.com/sharer/sharer.php?u=${url}&t=${title}`}
          onClick={() => {
            useGtm({
              event: "orTrack",
              eventName: "Article Shared",
              properties: {
                method: "Facebook",
                title,
              },
            })
          }}
          rel="noopener noreferrer"
          target="_blank"
          title="Share on Facebook"
        >
          <FaFacebook className={svgClassName} />
        </a>
      </li>
      <li>
        <a
          aria-label="Share on Twitter"
          className={linkButtonClassName}
          href={`http://www.twitter.com/intent/tweet?url=${url}&via=OnyxandRoseCBD&text=${title}`}
          onClick={() => {
            useGtm({
              event: "orTrack",
              eventName: "Article Shared",
              properties: {
                method: "Twitter",
                title,
              },
            })
          }}
          rel="noopener noreferrer"
          target="_blank"
          title="Share on Twitter"
        >
          <FaTwitterSquare className={svgClassName} />
        </a>
      </li>
      <li className="relative">
        <button
          aria-label="Copy link to clipboard"
          className={linkButtonClassName}
          onClick={copyUrlToClipboard}
          title="Copy link to clipboard"
        >
          <FaLink className={svgClassName} />
        </button>
        <textarea
          className={textAreaClassName}
          defaultValue={url}
          ref={copyRef}
          tabIndex={-1}
        />
        <div
          aria-hidden={!isCopied}
          data-journal-share-bar-showing={isCopied}
          className={classNames(
            "absolute",
            "bg-black",
            "font-sans",
            "left-0",
            "opacity-0",
            "px-4",
            "pb-3",
            "pt-4",
            "pointer-events-none",
            "rounded",
            "text-sm",
            "text-white",
            "top-full",
            "transition-all",
            "translate-y-[10%]",
            "whitespace-nowrap",
            "[z-index:2]",
            "before:border-solid",
            "before:border-l-4",
            "before:border-l-transparent",
            "before:border-r-4",
            "before:border-r-transparent",
            "before:border-b-8",
            "before:border-b-black",
            "before:bottom-full",
            "before:content-['']",
            "before:block",
            "before:h-0",
            "before:left: 4px;",
            "before:absolute",
            "before:w-0"
          )}
        >
          Link copied to clipboard
        </div>
      </li>
    </ul>
  )
}
